<script lang="ts">
interface DataTableContentProps<TData> extends ClassProps {
	stickyHeader?: boolean;
	manualSizing?: boolean;
	rowDisabled?: (row: DataTableRow<TData>) => boolean;
	rowClasses?: (row: DataTableRow<TData>) => any;
}
</script>

<script setup lang="ts" generic="TData extends RowData">
import { FlexRender, type RowData } from "@tanstack/vue-table";
import { computed, getCurrentInstance, watch } from "vue";
import type { ClassProps } from "../../lib/base-props";
import { cn } from "../../lib/utils";
import { useLocale } from "../../locale";
import { injectDataCollectionContext } from "../data-collection/composables/data-collection";
import Progress from "../progress/Progress.vue";
import { useColumns } from "./columns";
import DataTableColumnHeader from "./DataTableColumnHeader.vue";
import { injectDataTableRootContext, type DataTableRow } from "./DataTableRoot.vue";
import { injectDataTableVirtualizerContext } from "./DataTableVirtualizer.vue";

const { t } = useLocale();

defineOptions({ inheritAttrs: false });

const props = defineProps<DataTableContentProps<TData>>();
const emit = defineEmits<{
	"click:row": [row: DataTableRow<TData>];
}>();

const { manager, items } = injectDataCollectionContext();
const { columns: tableColumns, ready, loading, styled } = injectDataTableRootContext();

const columns = useColumns();

watch(
	columns,
	(columns) => {
		if (columns.length != 0) {
			tableColumns.value = columns;

			requestAnimationFrame(() => {
				ready.value = true;
			});
		}
	},
	{ immediate: true }
);

const virtualizerCtx = injectDataTableVirtualizerContext(null);

const rows = computed(() => {
	if (virtualizerCtx != null) {
		return virtualizerCtx.virtualRows.value;
	} else {
		return items.value;
	}
});

const tableColCount = computed(() => {
	let count = 0;

	for (const hg of manager.getHeaderGroups()) {
		for (const h of hg.headers) {
			if (!h.column.columnDef.meta?.hidden) {
				count += h.colSpan;
			}
		}
	}

	return count;
});

const hasRowClickListener = computed(() => !!getCurrentInstance()?.vnode.props?.["onClick:row"]);

// Prevent click:row event emit if the target is an interactable element
function clickRow(evt: MouseEvent, row: DataTableRow<any>) {
	if (evt.target instanceof HTMLButtonElement || evt.target instanceof HTMLInputElement) {
		return;
	}

	emit("click:row", row);
}
</script>

<template>
	<table v-if="ready" :class="cn('table', styled && 'table-styled', props.class)" v-bind="$attrs">
		<thead :class="[stickyHeader && 'sticky top-0 z-10']">
			<tr v-for="hg in manager.getHeaderGroups()" :key="hg.id">
				<template v-for="h of hg.headers" :key="h.id">
					<th
						v-if="!h.column.columnDef.meta?.hidden"
						:class="
							cn(
								h.column.columnDef.meta?.shrink && 'w-px whitespace-nowrap',
								h.column.columnDef.meta?.class
							)
						"
						:style="h.column.columnDef.meta?.style"
					>
						<DataTableColumnHeader :header="h">
							<FlexRender :render="h.column.columnDef.header" :props="h.getContext()" />
						</DataTableColumnHeader>
					</th>
				</template>
			</tr>
		</thead>
		<tbody>
			<tr
				v-if="virtualizerCtx?.topPadding.value"
				:style="{
					height: `${virtualizerCtx.topPadding.value}px`,
				}"
			>
				<td :colspan="manager.getVisibleFlatColumns().length" />
			</tr>
			<template v-if="rows.length">
				<tr
					v-for="row of rows"
					:key="row.row.id"
					:style="'style' in row ? row.style : undefined"
					:class="
						cn(
							{
								'cursor-pointer': hasRowClickListener,
								'text-muted cursor-default': props.rowDisabled?.(row) ?? false,
							},
							props.rowClasses?.(row)
						)
					"
					:data-state="row.row.getIsSelected() && 'selected'"
					@click="(evt) => clickRow(evt, row)"
				>
					<template v-for="cell of row.row.getVisibleCells()" :key="cell.id">
						<td v-if="!cell.column.columnDef.meta?.hidden">
							<FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
						</td>
					</template>
				</tr>
			</template>

			<tr v-else-if="loading">
				<td :colspan="tableColCount">
					<slot name="loading">
						<Progress color="primary" />
					</slot>
				</td>
			</tr>

			<tr v-else data-empty>
				<td :colspan="tableColCount">
					<slot name="empty">
						{{ t("table.no-data-available") }}
					</slot>
				</td>
			</tr>

			<tr
				v-if="virtualizerCtx?.bottomPadding.value"
				:style="{
					height: `${virtualizerCtx.bottomPadding.value}px`,
				}"
			>
				<td :colspan="manager.getVisibleFlatColumns().length" />
			</tr>
		</tbody>
	</table>
	<slot />
</template>
